import { createContext, useContext, useRef, useCallback, type ReactNode, type MutableRefObject } from "react";
import type { UseDrawingSelectionReturn } from "../hooks/useDrawingSelection";

export interface DrawingSelectionContextValue {
  drawingSelection: UseDrawingSelectionReturn;
  clearAllDrawings: () => void;
  setClearAllDrawings: (fn: () => void) => void;
}

const DrawingSelectionContext = createContext<DrawingSelectionContextValue | null>(null);

interface DrawingSelectionProviderProps {
  drawingSelection: UseDrawingSelectionReturn;
  /** Optional external ref that tracks clearAllDrawings for parent access */
  clearAllDrawingsRef?: MutableRefObject<() => void>;
  children: ReactNode;
}

export function DrawingSelectionProvider({ drawingSelection, clearAllDrawingsRef, children }: DrawingSelectionProviderProps) {
  const clearRef = useRef<() => void>(() => {});

  const clearAllDrawings = useCallback(() => {
    clearRef.current();
  }, []);

  const setClearAllDrawings = useCallback((fn: () => void) => {
    clearRef.current = fn;
    if (clearAllDrawingsRef) clearAllDrawingsRef.current = fn;
  }, [clearAllDrawingsRef]);

  return (
    <DrawingSelectionContext.Provider value={{ drawingSelection, clearAllDrawings, setClearAllDrawings }}>
      {children}
    </DrawingSelectionContext.Provider>
  );
}

export function useDrawingSelectionContext(): DrawingSelectionContextValue {
  const ctx = useContext(DrawingSelectionContext);
  if (!ctx) {
    throw new Error("useDrawingSelectionContext must be used within a DrawingSelectionProvider");
  }
  return ctx;
}
